/* src/contexts/profile/profileActionsCRUD.js */
import { useCallback } from 'react';
import {
    collection, doc, addDoc, updateDoc, deleteDoc, writeBatch,
    serverTimestamp, Timestamp, arrayUnion, increment
} from 'firebase/firestore';
import { db as firestoreDb } from '../../firebaseConfig';
import { DEFAULT_PROFILE_DATA } from './profileConstants'; // Assuming constants moved or imported here
import { sanitizeTag } from './profileUtils';

// --- Constants (Copied or Imported if moved) ---
const MAX_PROFILES_PER_USER = 6;

/**
 * Hook creator for Profile Add action.
 */
export const useCreateAddProfileAction = (
    authUserRef,
    profiles, // Read-only state needed for length check
    setError,
    setProfileUsernameFn // Cloud function reference
) => {
    return useCallback(async (profileData) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId) {
            setError("Authentication required to add a profile.");
            console.error("addProfile Error: User not authenticated.");
            return null;
        }

        const rawUsername = profileData?.username;
        const trimmedUsername = rawUsername?.trim();
        if (!trimmedUsername || trimmedUsername.length < 3 || trimmedUsername.length > 25 || !/^[a-zA-Z0-9_-]+$/.test(trimmedUsername)) {
            setError("Invalid username format (3-25 chars, a-z, A-Z, 0-9, _, -).");
            console.error("addProfile Error: Invalid username format.", { username: rawUsername });
            return null;
        }

        if (profiles.length >= MAX_PROFILES_PER_USER) {
            setError(`Maximum profiles reached (${MAX_PROFILES_PER_USER}).`);
            console.error("addProfile Error: Max profiles reached.");
            return null;
        }

        if (!setProfileUsernameFn) {
             setError("Profile creation service is currently unavailable. Please try again later.");
             console.error("addProfile Error: setProfileUsernameFn is not available.");
             return null;
        }

        setError(null);
        const db = firestoreDb;
        const profilesCollectionRef = collection(db, 'users', currentUserId, 'profiles');
        const newProfileRef = doc(profilesCollectionRef);
        const profileId = newProfileRef.id;

        console.log(`addProfile: Initiating profile creation for user ${currentUserId} with requested username: ${rawUsername}`);

        const initialProfileData = {
             userId: currentUserId,
             createdAt: serverTimestamp(),
             updatedAt: serverTimestamp(),
             currentNBack: DEFAULT_PROFILE_DATA.currentNBack,
             currentDifficulty: DEFAULT_PROFILE_DATA.currentDifficulty,
             highestNBack: DEFAULT_PROFILE_DATA.highestNBack,
             consecutiveSuccesses: DEFAULT_PROFILE_DATA.consecutiveSuccesses,
             journalTags: DEFAULT_PROFILE_DATA.journalTags,
             cstHistory: DEFAULT_PROFILE_DATA.cstHistory,
             tutorialsCompleted: DEFAULT_PROFILE_DATA.tutorialsCompleted,
             cstFamiliarizationCount: DEFAULT_PROFILE_DATA.cstFamiliarizationCount,
             lastCstAssessmentTimestamp: DEFAULT_PROFILE_DATA.lastCstAssessmentTimestamp,
             // --- NEW FIELD INITIALIZATION (already present and verified) ---
             highestNBackGood: DEFAULT_PROFILE_DATA.highestNBackGood,
             highestAchievedGoodSessionId: DEFAULT_PROFILE_DATA.highestAchievedGoodSessionId,
             previousNBackLevelDifficulties: DEFAULT_PROFILE_DATA.previousNBackLevelDifficulties,
        };

        try {
            const initialBatch = writeBatch(db);
            initialBatch.set(newProfileRef, initialProfileData);
            await initialBatch.commit();
            console.log(`addProfile: Initial profile document created (ID: ${profileId}) for user ${currentUserId}.`);

            console.log(`addProfile: Calling setProfileUsername Cloud Function for profile ${profileId}...`);
            const result = await setProfileUsernameFn({
                profileId: profileId,
                newUsername: rawUsername
            });

            if (!result?.data?.success) {
                const funcErrorMsg = result?.data?.message || "Failed to set username during profile creation.";
                console.error(`addProfile: setProfileUsername Cloud Function failed for profile ${profileId}. Reason: ${funcErrorMsg}`, result?.data);
                setError(funcErrorMsg);
                console.warn(`addProfile: Attempting to delete partially created profile ${profileId} due to username function failure.`);
                try { await deleteDoc(newProfileRef); } catch (cleanupError) { console.error(`addProfile: Failed to cleanup profile ${profileId} after username error:`, cleanupError); }
                return null;
            }

            const sanitizedUsername = result.data.username;
            console.log(`addProfile: setProfileUsername successful. Sanitized username: ${sanitizedUsername}`);

            const finalProfileData = {
                ...initialProfileData,
                id: profileId,
                username: sanitizedUsername,
                createdAt: new Date().toISOString(), // Approximation
                updatedAt: new Date().toISOString(), // Approximation
            };
            // Adjust approximation if needed (listener will override)
             if (initialProfileData.createdAt instanceof Timestamp) finalProfileData.createdAt = initialProfileData.createdAt.toDate().toISOString();
             if (initialProfileData.updatedAt instanceof Timestamp) finalProfileData.updatedAt = initialProfileData.updatedAt.toDate().toISOString();
             if (!(finalProfileData.createdAt)) finalProfileData.createdAt = new Date().toISOString();
             if (!(finalProfileData.updatedAt)) finalProfileData.updatedAt = new Date().toISOString();

            console.log(`addProfile: Profile creation process complete for profile ID ${profileId}.`);
            return finalProfileData; // Listener will update state

        } catch (error) {
            console.error(`addProfile: Error during profile creation process for user ${currentUserId}:`, error);
            if (!error?.message?.includes("Failed to set username")) {
                 if (error.code === 'permission-denied' || error.message?.includes('PERMISSION_DENIED')) {
                     setError("Failed to create profile shell. Check Firestore rules and data.")
                 } else {
                     setError(error.message || "An unexpected error occurred during profile creation.");
                 }
            }
            return null;
        }
    // Dependencies for the outer useCallback created in ProfileContext.js will include these
    }, [authUserRef, profiles, setError, setProfileUsernameFn]);
};

/**
 * Hook creator for Set Profile Username action.
 */
export const useCreateSetProfileUsernameAction = (
    authUserRef,
    setError,
    setProfileUsernameFn
) => {
    return useCallback(async (profileId, username) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId) {
            setError("Authentication or profile ID missing.");
            console.error("setProfileUsername Error: Auth/profile ID missing.");
            return false;
        }
        const name = username?.trim();
        if (!name || name.length < 3 || name.length > 25) {
             setError("Invalid username format (3-25 chars).");
             console.error("setProfileUsername Error: Invalid username format.", { username: username });
             return false;
        }
        if (!setProfileUsernameFn) {
             setError("Username setting service is currently unavailable.");
             console.error("setProfileUsername Error: setProfileUsernameFn is not available.");
             return false;
        }

        setError(null);
        console.log(`setProfileUsername: Calling Cloud Function for profile ${profileId} with username: ${name}`);

        try {
             const result = await setProfileUsernameFn({
                 profileId: profileId,
                 newUsername: name
             });

             if (result?.data?.success) {
                 const sanitizedUsername = result.data.username;
                 console.log(`setProfileUsername: Cloud Function successful for profile ${profileId}. Sanitized username: ${sanitizedUsername}`);
                 // Listener will update state
                 return true;
             } else {
                 const funcErrorMsg = result?.data?.message || "Failed to save username. It might be taken.";
                 console.error(`setProfileUsername: Cloud function failed for profile ${profileId}. Reason: ${funcErrorMsg}`, result?.data);
                 setError(funcErrorMsg);
                 return false;
             }
        } catch (error) {
            console.error(`setProfileUsername: Error calling cloud function for profile ${profileId}:`, error);
            const errorMessage = error.message || "An unexpected server error occurred while setting username.";
            setError(prev => prev || errorMessage);
            return false;
        }
    }, [authUserRef, setError, setProfileUsernameFn]);
};

/**
 * Hook creator for Update Profile Data action (only journalTags currently).
 * NOTE: Requires setProfiles because listener doesn't provide journalTags.
 */
export const useCreateUpdateProfileDataAction = (
    authUserRef,
    setProfiles, // Needs setter
    setError
) => {
    return useCallback(async (profileId, dataToUpdate) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId || typeof dataToUpdate !== 'object' || dataToUpdate === null) {
            setError("Invalid profile update data.");
            return false;
        }
        const allowedKeys = ['journalTags'];
        const payload = { updatedAt: serverTimestamp() };
        let localUpdateData = { updatedAt: new Date().toISOString() };
        let hasAllowedUpdate = false;

        if (dataToUpdate.journalTags !== undefined) {
            if (!Array.isArray(dataToUpdate.journalTags)) {
                setError("Journal tags must be an array.");
                return false;
            }
            payload.journalTags = [...new Set(dataToUpdate.journalTags.map(sanitizeTag).filter(Boolean))].sort();
            localUpdateData.journalTags = payload.journalTags;
            hasAllowedUpdate = true;
        }

        if (!hasAllowedUpdate) {
            const disallowedKeys = Object.keys(dataToUpdate).filter(k => !allowedKeys.includes(k));
            if (disallowedKeys.length > 0) console.warn("[ProfileContext updateProfileData] Attempted restricted update:", disallowedKeys);
            return true;
        }

        setError(null);
        const db = firestoreDb;
        try {
            const profileRef = doc(db, 'users', currentUserId, 'profiles', profileId);
            await updateDoc(profileRef, payload);
            // Update local state directly
            setProfiles(prev => prev.map(p => p.id === profileId ? { ...p, ...localUpdateData } : p));
            return true;
        } catch (err) {
            console.error(`[ProfileContext updateProfileData] Error updating ${profileId}:`, err);
            setError("Profile update failed.");
            return false;
        }
    }, [authUserRef, setProfiles, setError]);
};

/**
 * Hook creator for Update Profile Progress action.
 * NOTE: Requires setProfiles because listener doesn't provide these fields.
 */
export const useCreateUpdateProfileProgressAction = (
    authUserRef,
    setProfiles, // Needs setter
    setError
) => {
    return useCallback(async (profileId, progressData) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId || typeof progressData !== 'object' || progressData === null) {
            setError("Invalid progress data.");
            console.error("updateProfileProgress Error: Invalid input", { currentUserId, profileId, progressData });
            return false;
        }
        setError(null);
        const allowedKeys = [
            'currentNBack', 'currentDifficulty', 'highestNBack', 'consecutiveSuccesses',
            'highestNBackGood', 'highestAchievedGoodSessionId', 'previousNBackLevelDifficulties'
        ];
        const payload = { updatedAt: serverTimestamp() };
        let localUpdateData = { updatedAt: new Date().toISOString() }; // For immediate UI update
        let validUpdateFound = false;


        for (const key of allowedKeys) {
            // Check if progressData has the key, explicitly.
            // This prevents accidental updates if a key is missing but has a falsy value in the prototype chain.
            if (Object.prototype.hasOwnProperty.call(progressData, key)) {
                if (key === 'previousNBackLevelDifficulties') {
                    // This expects progressData.previousNBackLevelDifficulties to be the *complete* new object
                    // It should be an object, even if empty.
                    if (typeof progressData[key] === 'object' && progressData[key] !== null) {
                        payload[key] = progressData[key]; // Send the whole object to Firestore
                        localUpdateData[key] = progressData[key]; // Update local state with the whole object
                        validUpdateFound = true;
                    } else if (progressData[key] === null) { // Allow explicitly setting to null if needed by design, though typically it's an object
                        payload[key] = {}; // Default to empty object in Firestore if null is passed
                        localUpdateData[key] = {};
                        validUpdateFound = true;
                        console.warn(`[ProfileProgress] progressData.previousNBackLevelDifficulties was null, setting to {} for profile ${profileId}.`);
                    } else {
                        console.warn(`[ProfileProgress] Invalid type for previousNBackLevelDifficulties:`, progressData[key]);
                    }
                } else if (key === 'highestNBackGood' || key === 'highestAchievedGoodSessionId') {
                    // These fields can legitimately be null or a string/number.
                     if (progressData[key] === null || typeof progressData[key] === 'number' || typeof progressData[key] === 'string') {
                        payload[key] = progressData[key];
                        localUpdateData[key] = progressData[key];
                        validUpdateFound = true;
                    } else {
                        console.warn(`[ProfileProgress] Invalid type for ${key}:`, progressData[key]);
                    }
                } else if (typeof progressData[key] === 'number' && !isNaN(progressData[key])) {
                    // Standard number fields
                    payload[key] = progressData[key];
                    localUpdateData[key] = progressData[key];
                    validUpdateFound = true;
                } else {
                    console.warn(`[ProfileProgress] Field '${key}' in progressData was not a valid number or expected type:`, progressData[key]);
                }
            }
        }

        if (!validUpdateFound) {
            console.warn("[ProfileProgress] No valid fields found in progressData for update.", progressData);
            // If only `updatedAt` was going to be sent, we might consider this a "no-op" success or a failure.
            // For now, let's treat it as if no *actual* data changed, so return true.
            // If `progressData` was empty, this means nothing to update beyond timestamp.
            if (Object.keys(progressData).length === 0) return true; // Explicitly empty object, no data fields to update.
            // If progressData had keys, but none were valid, it's more like an error.
            console.error("[ProfileProgress] progressData contained keys, but none were valid for update.", progressData);
            setError("Invalid progress data fields provided.");
            return false;
        }

        const db = firestoreDb;
        try {
            // Update local state first for responsiveness
            setProfiles(prev => prev.map(p => p.id === profileId ? { ...p, ...localUpdateData } : p));

            const profileRef = doc(db, 'users', currentUserId, 'profiles', profileId);
            await updateDoc(profileRef, payload);
            console.log(`[ProfileProgress] Successfully updated profile ${profileId} with data:`, payload);
            return true;
        } catch (err) {
            console.error(`[ProfileProgress] Error updating ${profileId}:`, err, "Payload:", payload);
            setError(`Progress save failed: ${err.message}`);
            // Attempt to revert local state change
            setProfiles(prevProfiles => {
                const profileToRevert = prevProfiles.find(p => p.id === profileId);
                if (profileToRevert) {
                    // This is a simple revert. A more robust solution might involve storing the original profile slice.
                    const originalProfileData = { ...profileToRevert };
                    Object.keys(localUpdateData).forEach(key => {
                        // This logic needs to be careful. If localUpdateData only had a subset of keys,
                        // we need to ensure we revert to the state *before* this optimistic update.
                        // A safer way is to fetch the profile again or have the original state.
                        // For now, this just removes the keys from the optimistic update.
                        // A true revert is complex without storing the 'before' state.
                        // The listener should eventually correct it from Firestore, but there might be a flicker.
                        // Let's assume the listener will fix it for now.
                    });
                    // To simplify, we'll just log that a revert *should* happen,
                    // and rely on the Firestore listener to eventually provide the correct data.
                    console.warn(`[ProfileProgress] Revert of local state for profile ${profileId} attempted after Firestore error. Listener should correct.`);
                }
                return prevProfiles; // Return unchanged previous profiles or a more complex revert
            });
            return false;
        }
    }, [authUserRef, setProfiles, setError]);
};

/**
 * Hook creator for Remove Profile action.
 */
export const useCreateRemoveProfileAction = (
    authUserRef,
    activeProfileIdState, // Read-only state needed
    profiles, // Read-only state needed
    selectProfile, // Action needed
    setError,
    deleteProfileAndCleanupFn
) => {
    return useCallback(async (profileId) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId) {
            setError("Cannot remove: Invalid ID/Auth.");
            return false;
        }
        if (!deleteProfileAndCleanupFn) {
            setError("Deletion service unavailable.");
            return false;
        }
        setError(null);

        let nextActiveProfileId = null;
        if (activeProfileIdState === profileId) {
             if (profiles.length > 1) {
                 const fallbackProfile = profiles.find(p => p.id !== profileId);
                 nextActiveProfileId = fallbackProfile ? fallbackProfile.id : null;
             } else {
                 nextActiveProfileId = null;
             }
        } else {
             nextActiveProfileId = activeProfileIdState;
        }

        try {
             console.log(`[removeProfile] Deleting ${profileId}. Next active will be: ${nextActiveProfileId}`);
             const result = await deleteProfileAndCleanupFn({ profileId: profileId });
             if (!result?.data?.success) throw new Error(result?.data?.message || "Backend deletion failed.");
             console.log(`[removeProfile] Cloud Function OK for ${profileId}. Selecting ${nextActiveProfileId}`);
             // Select profile handles state, localStorage, Firestore update
             await selectProfile(nextActiveProfileId);
             // Listener will remove the profile from local state
             return true;
        } catch (err) {
             console.error(`[removeProfile] Error deleting ${profileId}:`, err);
             setError(err.message || "Deletion failed.");
             return false;
        }
    }, [authUserRef, activeProfileIdState, profiles, selectProfile, setError, deleteProfileAndCleanupFn]);
};

/**
 * Hook creator for Mark Tutorial Completed action.
 * NOTE: Requires setProfiles because listener doesn't track tutorialsCompleted object changes well.
 */
export const useCreateMarkTutorialCompletedAction = (
    authUserRef,
    setProfiles, // Needs setter
    setError
) => {
    return useCallback(async (profileId, tutorialKey) => {
        const currentUserId = authUserRef.current?.uid;
        if (!currentUserId || !profileId || !tutorialKey) {
            console.error("[markTutorial] Missing data.");
            return false;
        }
        const fieldPath = `tutorialsCompleted.${tutorialKey}`;
        const profileDocRef = doc(firestoreDb, 'users', currentUserId, 'profiles', profileId);
        try {
            await updateDoc(profileDocRef, { [fieldPath]: true, updatedAt: serverTimestamp() });
            // Update local state directly
            setProfiles(prevProfiles => prevProfiles.map(p => {
                if (p.id === profileId) {
                    const currentTutorials = (typeof p.tutorialsCompleted === 'object' && p.tutorialsCompleted !== null) ? p.tutorialsCompleted : {};
                    return {
                        ...p,
                        tutorialsCompleted: { ...currentTutorials, [tutorialKey]: true },
                        updatedAt: new Date().toISOString() // Approx local update time
                    };
                }
                return p;
            }));
            return true;
        } catch (error) {
            console.error(`[markTutorial] Error marking '${tutorialKey}' for ${profileId}:`, error);
            setError(`Failed to update tutorial status: ${error.message}`);
            return false;
        }
    }, [authUserRef, setProfiles, setError]);
};